// packages/editor/src/studio/PublishModal.tsx
//
// Shows the diff, the changed files, an optional blast-radius indicator,
// and a commit-message textarea. Publish button dispatches via the
// SaveAdapter and reports the resulting commit SHA. E2 ships a simple
// "X files changed" indicator; richer blast radius (cells × restaurants)
// is a follow-up if the projector exposes the template→instance map.

import { useEffect, useState } from 'react';
import type { PublishResult, SaveAdapter } from '../save-adapters/types';

export interface PublishModalProps {
  adapter: SaveAdapter;
  paths: string[];
  onClose: () => void;
  onPublished: (result: PublishResult) => void;
}

export function PublishModal({ adapter, paths, onClose, onPublished }: PublishModalProps) {
  const [diffs, setDiffs] = useState<Map<string, string>>(new Map());
  const [message, setMessage] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all(paths.map(async (p) => [p, (await adapter.diff(p)).unifiedDiff] as const))
      .then((entries) => { if (!cancelled) setDiffs(new Map(entries)); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [adapter, paths]);

  const handlePublish = async () => {
    if (!message.trim()) return;
    setPublishing(true);
    setError(null);
    try {
      const result = await adapter.publish(paths, message.trim());
      onPublished(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      data-testid="publish-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div style={{
        background: '#ffffff', width: 720, maxHeight: '90vh', borderRadius: 8,
        padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Publish changes</h2>
          <button data-testid="publish-close" onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </header>

        <div data-testid="publish-files-list">
          <strong>{paths.length} file{paths.length === 1 ? '' : 's'} changed:</strong>
          <ul style={{ marginTop: 4 }}>
            {paths.map((p) => <li key={p} style={{ fontFamily: 'monospace', fontSize: 12 }}>{p}</li>)}
          </ul>
        </div>

        <details>
          <summary style={{ cursor: 'pointer' }}>Diff preview</summary>
          {[...diffs.entries()].map(([p, d]) => (
            <div key={p} style={{ marginTop: 8 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{p}</div>
              <pre style={{ fontSize: 11, lineHeight: 1.4, background: '#fafafa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                {d || '(no diff)'}
              </pre>
            </div>
          ))}
        </details>

        <textarea
          data-testid="publish-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Commit message — describe what changed and why"
          style={{ minHeight: 80, padding: 8, border: '1px solid #d1d5db', borderRadius: 4, fontFamily: 'system-ui', fontSize: 13 }}
        />

        {error && <p data-testid="publish-error" style={{ color: 'crimson', margin: 0 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={publishing} style={{ padding: '6px 16px' }}>Cancel</button>
          <button
            data-testid="publish-confirm"
            onClick={handlePublish}
            disabled={publishing || !message.trim()}
            style={{ padding: '6px 16px', background: '#4a6fa5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
